<script setup>
import { nextTick, onMounted, ref, watch } from 'vue'
import { grow } from '@/lib/autogrow.js'

/**
 * A descriptive text — a name, a title, a caption, a note — in a field that wraps its words and
 * grows with them (as every field of many lines does, D-251), so a long one is read whole instead
 * of scrolling inside one line; empty or short, it is one line high. `oneLine` (the default) keeps
 * it one paragraph: Enter adds no break and a pasted break becomes a space, the value as a
 * one-line input would hold it; without it, breaks stay (a description).
 *
 * The parent's own input and change listeners reach the textarea as they would an input.
 */
const props = defineProps({
  modelValue: { type: String, default: '' },
  oneLine: { type: Boolean, default: true },
  rows: { type: Number, default: 1 },
})
const emit = defineEmits(['update:modelValue'])
const el = ref(null)

function typed(e) {
  let v = e.target.value
  if (props.oneLine && /[\r\n]/.test(v)) {
    v = v.replace(/\s*[\r\n]+\s*/g, ' ')
    e.target.value = v
  }
  emit('update:modelValue', v)
}
function key(e) {
  if (props.oneLine && e.key === 'Enter') e.preventDefault()
}
// Where the browser does not size it by its content: sized when shown and when its text changes.
onMounted(() => grow(el.value))
watch(() => props.modelValue, () => nextTick(() => grow(el.value)))
</script>

<template>
  <textarea ref="el" :rows="rows" class="resize-none" :style="rows === 1 ? { minHeight: 0 } : null" :value="modelValue ?? ''" @input="typed" @keydown="key"></textarea>
</template>
